import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import AdminDashboardLayout from "../../../layouts/admin_dashboard_layout/admin_dashboard_layout";
import ReactQuill from "react-quill";
import { ArrowBack, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ref } from "firebase/storage";
import { getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { firestore, storage } from "../../..";
import {
  deaneryList,
  parishListMap,
  type DeaneryOption as Deanery,
  type ParishOption as Parish,
} from "../../../utils/deanery_parishes";
import { useFeedback } from "../../../providers/feedback/feedback";
import { buildNewsSlug } from "../../../utils/news";
import "react-quill/dist/quill.snow.css";

function UploadNews() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [postedBy, setPostedBy] = useState("");
  const [subtitle, setSubTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
  const submitLockRef = useRef(false);
  const [parishList, setParishList] = useState<Parish[]>([]);
  const { showSnackbar } = useFeedback();
  const [_selectedDeanery, _setSelectedDeanery] = useState<{
    id: Number;
    name: string;
  } | null>(null);
  const [_selectedParish, _setSelectedParish] = useState<{
    id: Number;
    value: string;
  } | null>(null);

  const handleDeaneryChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: Deanery | null,
  ) => {
    if (value) {
      _setSelectedDeanery({ id: value.id, name: value.name });
      setParishList(parishListMap[value.name] || []);
      _setSelectedParish(null);
    } else {
      _setSelectedDeanery(null);
      setParishList([]);
    }
  };

  const handleUpload = async () => {
    if (isSubmitting) {
      return;
    }
    if (submitLockRef.current) {
      return;
    }

    if (
      !title.trim() ||
      !postedBy.trim() ||
      !subtitle.trim() ||
      !description.trim() ||
      !selectedThumbnail
    ) {
      showSnackbar("Please fill all required fields");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSubmitting(true);
      const uploadKey = Date.now();

      const images = await Promise.all(
        selectedImages.map(async (image, index) => {
          const storageRef = ref(
            storage,
            `news/${uploadKey}-${index}-${image.name}`,
          );
          const uploadTask = uploadBytesResumable(storageRef, image);
          await uploadTask;
          return getDownloadURL(storageRef);
        }),
      );
      const storageRef = ref(
        storage,
        `news/${uploadKey}-thumbnail-${selectedThumbnail.name}`,
      );
      const uploadTaskForTuhumbail = uploadBytesResumable(
        storageRef,
        selectedThumbnail!,
      );
      await uploadTaskForTuhumbail;
      const downloadURL = await getDownloadURL(storageRef);
      const newsRef = await addDoc(collection(firestore, "news"), {
        thumbnail: downloadURL,
        title: title.trim(),
        subtitle: subtitle.trim(),
        gallery: images,
        description: description.trim(),
        deanerName: _selectedDeanery,
        selectedParishName: _selectedParish,
        id: new Date(),
        createdAt: new Date(),
        approved: true,
        postedBy: postedBy.trim(),
      });
      await updateDoc(doc(firestore, "news", newsRef.id), {
        slug: buildNewsSlug(title.trim(), newsRef.id),
      });
      showSnackbar("News uploaded successfully");
      setTitle("");
      setPostedBy("");
      setSubTitle("");
      setDescription("");
      setSelectedThumbnail(null);
      _setSelectedDeanery(null);
      _setSelectedParish(null);
      setParishList([]);
    } catch (error) {
      console.error("Error uploading images: ", error);
      showSnackbar("Something went wrong");
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
      setSelectedImages([]);
    }
  };

  return (
    <AdminDashboardLayout>
      <Box flex={1} mt={2} mx={2}>
        <Grid container>
          <Grid item md={6} lg={6} xl={6} pb={4}>
            <Card sx={{ width: "100%", borderRadius: 1, m: 3 }} elevation={1}>
              <CardContent>
                <Box display="flex" flexDirection="column" pl={1}>
                  <Box flexDirection="row" display="flex" alignItems="center">
                    <IconButton onClick={() => navigate(-1)}>
                      <ArrowBack></ArrowBack>
                    </IconButton>
                    <Typography variant="h6" fontWeight="600">
                      Add New News
                    </Typography>
                  </Box>
                  <Box>
                    <Box mt={3}>
                      <TextField
                        fullWidth
                        label="News posted by"
                        placeholder="Enter name"
                        value={postedBy}
                        onChange={(event) => {
                          setPostedBy(event?.target.value);
                        }}
                        required
                      />
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <TextField
                        fullWidth
                        label="Title of the news"
                        placeholder="Enter news title"
                        value={title}
                        onChange={(event) => {
                          setTitle(event?.target.value);
                        }}
                        required
                      />
                    </Box>
                    <Box mt={3}>
                      <TextField
                        fullWidth
                        rows={3}
                        multiline
                        label="Subtitle of the news"
                        placeholder="Enter news subtitle"
                        value={subtitle}
                        onChange={(event) => {
                          setSubTitle(event?.target.value);
                        }}
                        required
                      />
                    </Box>

                    <Box display={"flex"} mt={0} width={"100%"}>
                      <FormControl fullWidth margin="normal">
                        <FormLabel>Thumbnail *</FormLabel>
                        <input
                          type="file"
                          accept="image/*"
                          style={{
                            border: `1px solid rgb(181, 176, 176)`,
                            width: "96%",
                            paddingLeft: 8,
                            paddingRight: 8,
                            paddingTop: 16,
                            paddingBottom: 16,
                            borderRadius: 5,
                            marginTop: 8,
                          }}
                          onChange={(e) => {
                            if (e.target.files != null) {
                              const filesArray = e.target.files[0];
                              setSelectedThumbnail(filesArray);
                            }
                          }}
                        />
                        {selectedThumbnail && (
                          <Box
                            mt={2}
                            position="relative"
                            display="inline-block"
                          >
                            <img
                              src={URL.createObjectURL(selectedThumbnail)}
                              alt="Thumbnail Preview"
                              style={{
                                maxWidth: "200px",
                                maxHeight: "150px",
                                borderRadius: 5,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => setSelectedThumbnail(null)}
                              style={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                color: "#ffffff",
                                backgroundColor: "rgba(0,0,0,0.5)",
                              }}
                            >
                              <Close />
                            </IconButton>
                          </Box>
                        )}
                      </FormControl>
                    </Box>
                    <Box mt={2}>
                      <FormControl fullWidth margin="normal">
                        <FormLabel>Description of the news</FormLabel>
                        <ReactQuill
                          theme="snow"
                          value={description}
                          onChange={setDescription}
                          placeholder="Enter news description with formatting"
                          modules={{
                            toolbar: [
                              ["bold", "italic", "underline", "strike"],
                              ["blockquote", "code-block"],
                              [{ header: 1 }, { header: 2 }],
                              [{ list: "ordered" }, { list: "bullet" }],
                              [{ align: [] }],
                              ["link"],
                              ["clean"],
                            ],
                          }}
                          formats={[
                            "bold",
                            "italic",
                            "underline",
                            "strike",
                            "blockquote",
                            "code-block",
                            "header",
                            "list",
                            "align",
                            "link",
                          ]}
                          style={{ height: "300px", marginTop: "10px" }}
                        />
                      </FormControl>
                    </Box>
                    <Box display={"flex"} width={"100%"}>
                      <FormControl fullWidth margin="normal">
                        <FormLabel>
                          <Typography>Upload images</Typography>
                        </FormLabel>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          style={{
                            border: `1px solid rgb(181, 176, 176)`,
                            width: "96%",
                            paddingLeft: 8,
                            paddingRight: 8,
                            paddingTop: 16,
                            paddingBottom: 16,
                            borderRadius: 5,
                            marginTop: 8,
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (e.target.files != null) {
                              const filesArray = Array.from(e.target.files);
                              setSelectedImages((prevImages: File[]) => [
                                ...prevImages,
                                ...filesArray,
                              ]);
                            }
                          }}
                        />
                      </FormControl>
                    </Box>
                    <Box display={"flex"} flexDirection={"row"}>
                      {selectedImages.length > 0 &&
                        selectedImages?.map((file: File, index: number) => {
                          return (
                            <Box
                              display={"flex"}
                              flexDirection={"row"}
                              position={"relative"}
                            >
                              <img
                                src={URL.createObjectURL(selectedImages[index])}
                                alt="Preview"
                                style={{
                                  maxWidth: "100px",
                                  maxHeight: "80px",
                                  marginTop: "10px",
                                  marginRight: 8,
                                  borderRadius: 5,
                                }}
                              />
                              <Box
                                display={"flex"}
                                position={"absolute"}
                                left={0}
                                top={8}
                              >
                                <IconButton
                                  size={"small"}
                                  onClick={() => {
                                    let filter = selectedImages?.filter(
                                      (i: File) => i !== file,
                                    );
                                    setSelectedImages(filter);
                                  }}
                                  style={{ color: "#ffffff" }}
                                >
                                  <Close />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>
                    <Box mt={3}>
                      <Autocomplete
                        fullWidth
                        value={
                          deaneryList.find(
                            (option) => _selectedDeanery?.id === option.id,
                          ) || null
                        }
                        onChange={handleDeaneryChange}
                        options={deaneryList}
                        getOptionLabel={(option) => option.name || ""}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Deanery Name"
                          />
                        )}
                      />
                    </Box>
                    <Box mt={3}>
                      <Autocomplete
                        fullWidth
                        value={
                          parishList.find(
                            (option: Parish) =>
                              _selectedParish?.id === option.id,
                          ) || null
                        }
                        onChange={(
                          e: React.SyntheticEvent,
                          value: Parish | null,
                        ) => _setSelectedParish(value)}
                        options={parishList}
                        getOptionLabel={(option: Parish) => option.value || ""}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Parish name"
                          />
                        )}
                        disabled={!parishList.length}
                      />
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      mt={4}
                    >
                      <Button
                        onClick={() => navigate("/admin/users")}
                        variant="outlined"
                        sx={{
                          minWidth: 200,
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        onClick={() => {
                          handleUpload();
                        }}
                        sx={{
                          minWidth: 200,
                        }}
                        variant="contained"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Creating..." : "Create Post"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </AdminDashboardLayout>
  );
}

export default UploadNews;
